import { formatTimeOfDay, isoAttr } from "../../lib/datetime.ts";
import { type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import {
  DenseTable,
  StatusBadge,
  type Column,
} from "../../components/index.ts";
import {
  decisionLevel,
  eventDetailHref,
  severityLabel,
  severityLevel,
  type EventRow,
} from "./vocab.ts";

/**
 * The events table (task 11.12; design 12.2 live-tail explorer, 12.4 semantic tables). It renders a
 * list of redacted {@link EventRow}s through the shared semantic `DenseTable` — a real `<table>`
 * with a caption and column headers, readable by assistive tech. It is used for BOTH surfaces:
 *
 * - the LIVE TAIL (the stream view's events ring, after the client-side drill chips), which the
 *   caller passes newest-first and which grows as the client folds new deltas in; and
 * - the FROZEN / scrolled-back view (one page of `GET /events`), passed when paused or on a custom
 *   range.
 *
 * This component is presentational: the caller decides which events to show (live ring vs page) and
 * has already applied the client-side chips and the ordering. Each row's event-name cell is the
 * DRILL to that event's detail (`/events/{id}`); an event that carries a trace also links to its
 * cross-service waterfall (`/traces/{id}`). Severity and decision are shown icon+text (never colour
 * alone), and the sampled nature of an event is made explicit per row via its `sample_rate`.
 */
export function EventsTable(props: {
  events: readonly EventRow[];
  caption: string;
  emptyMessage?: string;
}): ReactNode {
  const navigate = useNavigate();

  const columns: readonly Column<EventRow>[] = [
    {
      key: "time",
      header: "Time",
      width: "sm",
      cell: (e) => (
        <time
          className="evt-num"
          dateTime={isoAttr(e.ts)}
          title={`event ${isoAttr(e.ts)} · received ${isoAttr(e.recv_ts)}`}
        >
          {formatTimeOfDay(e.ts)}
        </time>
      ),
    },
    {
      key: "name",
      header: "Event",
      isRowHeader: true,
      cell: (e) => (
        <button
          type="button"
          className="evt-link"
          onClick={() => navigate(eventDetailHref(e.id))}
          aria-label={`Open the detail for event ${e.name} (${e.id})`}
          title={e.id}
        >
          <span className="evt-link__name">{e.name}</span>
        </button>
      ),
    },
    {
      key: "component",
      header: "Component",
      cell: (e) => <span className="evt-component">{e.service}</span>,
    },
    {
      key: "severity",
      header: "Severity",
      cell: (e) => (
        <StatusBadge
          level={severityLevel(e.severity)}
          label={severityLabel(e.severity)}
          size="sm"
          title={`Severity: ${severityLabel(e.severity)}`}
        />
      ),
    },
    {
      key: "decision",
      header: "Decision",
      cell: (e) =>
        e.decision === null || e.decision === undefined ? (
          <span className="evt-faint">n/a</span>
        ) : (
          <StatusBadge
            level={decisionLevel(e.decision)}
            label={e.decision === "deny" ? "Deny" : "Allow"}
            size="sm"
            title={
              e.reason !== null && e.reason !== undefined
                ? `Decision: ${e.decision}: ${e.reason}`
                : `Decision: ${e.decision}`
            }
          />
        ),
    },
    {
      key: "agent",
      header: "Agent",
      cell: (e) =>
        e.agent_sub === null || e.agent_sub === undefined ? (
          <span className="evt-faint">n/a</span>
        ) : (
          <code className="evt-id" title={e.agent_sub}>
            {shortId(e.agent_sub)}
          </code>
        ),
    },
    {
      key: "trace",
      header: "Trace",
      cell: (e) =>
        e.trace_id === null || e.trace_id === undefined ? (
          <span className="evt-faint">n/a</span>
        ) : (
          <button
            type="button"
            className="evt-link evt-link--trace"
            onClick={() => navigate(`/traces/${encodeURIComponent(e.trace_id as string)}`)}
            aria-label={`Open the trace ${e.trace_id} for this event`}
            title={e.trace_id}
          >
            <code>{shortId(e.trace_id)}</code>
          </button>
        ),
    },
    {
      key: "sample",
      header: "Sampled",
      align: "right",
      cell: (e) =>
        // sample_rate is in (0, 1]; a rate below 1 means this event stands for 1/rate events, so
        // any count built from it is an estimate (the per-row honesty behind the page's "est." count).
        e.sample_rate >= 1 ? (
          <span className="evt-faint" title="Fully sampled (1:1)">
            1:1
          </span>
        ) : (
          <span
            className="evt-sampled"
            title={`Sampled at ${e.sample_rate}, represents about ${Math.round(1 / e.sample_rate)} events`}
          >
            1:{Math.round(1 / e.sample_rate)} est.
          </span>
        ),
    },
  ];

  return (
    <DenseTable
      caption={props.caption}
      columns={columns}
      rows={props.events}
      rowKey={(e) => e.id}
      stickyHeader
      emptyMessage={props.emptyMessage ?? "No events match the current filters in this window."}
    />
  );
}

/** A short, readable form of a long id for a row (full id is the cell's title/aria). */
function shortId(id: string): string {
  return id.length <= 12 ? id : `${id.slice(0, 8)}…`;
}
